import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { IOrganization } from '@/domain/entities/organization';
import type { OrganizationsState } from './organizationTypes';

import {
  checkSlug,
  createOrganization,
  fetchOrganization,
  fetchOrgMemberships,
  fetchUserMemberships,
  listOrganizations,
  updateOrganizationMember,
  updateOrganization,
} from './organizationsThunks';
import type { RootState } from '@/store/root-state';

const initialState: OrganizationsState = {
  items: [],
  isLoading: false,
  hasFetched: false,
  hasListedAllOrganizations: false,
  error: null,
  activeOrganizationId: null,
  fetchingById: {},
  errorById: {},
  hasFetchedById: {},

  slugChecks: {},
  slugChecksLoading: {},
  slugChecksError: {},

  membersByOrgId: {},
  membershipsByMemberId: {},

  fetchingMembersByOrgId: {},
  fetchingMembersErrorByOrgId: {},
  hasFetchedMembersByOrgId: {},

  fetchingMembershipsByMemberId: {},
  fetchingMembershipsErrorByMemberId: {},
  hasFetchedMembershipsByMemberId: {},
};

/** Upsert helpers */
function upsertOne(items: IOrganization[], next: IOrganization) {
  const idx = items.findIndex((x) => x.id === next.id);
  if (idx >= 0) items[idx] = next;
  else items.unshift(next);
}

function upsertMany(items: IOrganization[], incoming: IOrganization[]) {
  for (const org of incoming) upsertOne(items, org);
}

function toMembershipKey<T extends { id: string; memberID: string }>(item: T) {
  return `${item.id.trim()}::${item.memberID.trim()}`;
}

function upsertMembershipInList<T extends { id: string; memberID: string }>(items: T[], next: T) {
  const nextKey = toMembershipKey(next);
  const idx = items.findIndex((x) => toMembershipKey(x) === nextKey);

  if (idx >= 0) {
    items[idx] = next;

    for (let i = items.length - 1; i >= 0; i -= 1) {
      if (i !== idx && toMembershipKey(items[i]) === nextKey) {
        items.splice(i, 1);
      }
    }
    return;
  }

  items.unshift(next);
}

function toSlugKey(slug: string) {
  return slug.trim().toLowerCase();
}

function toOrgKey(orgId: string) {
  return orgId.trim();
}

function toMemberKey(memberID: string) {
  return memberID.trim();
}

export const organizationsSlice = createSlice({
  name: 'organizations',
  initialState,
  reducers: {
    resetOrganizations: () => ({ ...initialState }),

    setOrganizations: (state, action: PayloadAction<IOrganization[]>) => {
      state.items = action.payload;
      state.hasFetched = true;
      state.error = null;
    },

    upsertOrganization: (state, action: PayloadAction<IOrganization>) => {
      upsertOne(state.items, action.payload);
      state.error = null;
    },

    upsertOrganizations: (state, action: PayloadAction<IOrganization[]>) => {
      upsertMany(state.items, action.payload);
      state.error = null;
    },

    removeOrganization: (state, action: PayloadAction<{ id: string }>) => {
      state.items = state.items.filter((x) => x.id !== action.payload.id);
    },

    setOrganizationsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    setOrganizationsError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    setActiveOrganizationId: (state, action: PayloadAction<string | null>) => {
      state.activeOrganizationId = action.payload;
    },

    clearSlugChecks: (state) => {
      state.slugChecks = {};
      state.slugChecksLoading = {};
      state.slugChecksError = {};
    },

    // Optional utilities
    clearOrgMembersCache: (state, action: PayloadAction<{ orgId: string }>) => {
      const orgKey = toOrgKey(action.payload.orgId);
      delete state.membersByOrgId[orgKey];
      delete state.fetchingMembersByOrgId[orgKey];
      delete state.fetchingMembersErrorByOrgId[orgKey];
      delete state.hasFetchedMembersByOrgId[orgKey];
    },

    clearUserMembershipsCache: (state, action: PayloadAction<{ memberID: string }>) => {
      const memberKey = toMemberKey(action.payload.memberID);
      delete state.membershipsByMemberId[memberKey];
      delete state.fetchingMembershipsByMemberId[memberKey];
      delete state.fetchingMembershipsErrorByMemberId[memberKey];
      delete state.hasFetchedMembershipsByMemberId[memberKey];
    },
  },

  extraReducers: (builder) => {
    builder
      // ========== CREATE ORG ==========
      .addCase(createOrganization.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createOrganization.fulfilled, (state, action) => {
        state.isLoading = false;
        state.hasFetched = true;

        const org = action.payload.data.organization;
        if (org) upsertOne(state.items, org);

        // NOTE: If your create-org API returns membership/role/user too,
        // you can hydrate caches here later.
      })
      .addCase(createOrganization.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || action.error.message || 'Failed to create organization';
      })

      // ========== FETCH ORG ==========
      .addCase(fetchOrganization.pending, (state, action) => {
        const orgKey = toOrgKey(action.meta.arg.orgId);
        // Keep the existing global loading flag for older selectors, but track profile loads per org too.
        state.isLoading = true;
        state.error = null;
        state.fetchingById ??= {};
        state.errorById ??= {};
        state.fetchingById[orgKey] = true;
        state.errorById[orgKey] = null;
      })
      .addCase(fetchOrganization.fulfilled, (state, action) => {
        const orgKey = toOrgKey(action.meta.arg.orgId);
        state.isLoading = false;
        state.hasFetched = true;
        state.error = null;
        state.fetchingById ??= {};
        state.errorById ??= {};
        state.hasFetchedById ??= {};
        state.fetchingById[orgKey] = false;
        state.errorById[orgKey] = null;
        state.hasFetchedById[orgKey] = true;
        upsertOne(state.items, action.payload);
      })
      .addCase(fetchOrganization.rejected, (state, action) => {
        if (action.meta.condition) return;

        const orgKey = toOrgKey(action.meta.arg.orgId);
        state.isLoading = false;
        const message = action.payload?.message || action.error.message || 'Failed to fetch organization';
        state.error = message;
        state.fetchingById ??= {};
        state.errorById ??= {};
        state.hasFetchedById ??= {};
        state.fetchingById[orgKey] = false;
        state.errorById[orgKey] = message;
        state.hasFetchedById[orgKey] = true;
      })

      // ========== UPDATE ORG ==========
      .addCase(updateOrganization.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateOrganization.fulfilled, (state, action) => {
        state.isLoading = false;
        state.hasFetched = true;

        const org = action.payload.data;
        if (org) upsertOne(state.items, org);
      })
      .addCase(updateOrganization.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || action.error.message || 'Failed to update organization';
      })

      // ========== CHECK SLUG ==========
      .addCase(checkSlug.pending, (state, action) => {
        const slugKey = toSlugKey(action.meta.arg.slug);
        state.slugChecksLoading[slugKey] = true;
        state.slugChecksError[slugKey] = null;
      })
      .addCase(checkSlug.fulfilled, (state, action) => {
        const slugKey = toSlugKey(action.payload.slug);
        state.slugChecksLoading[slugKey] = false;
        state.slugChecksError[slugKey] = null;
        state.slugChecks[slugKey] = {
          available: action.payload.available,
          checkedAt: Date.now(),
        };
      })
      .addCase(checkSlug.rejected, (state, action) => {
        const slugKey = toSlugKey(action.meta.arg.slug);
        state.slugChecksLoading[slugKey] = false;
        if (action.meta.condition) return;
        state.slugChecksError[slugKey] = action.payload?.message || action.error.message || 'Failed to check slug';
      })

      // =========================
      // MEMBERSHIPS: ORG -> MEMBERS
      // =========================
      .addCase(fetchOrgMemberships.pending, (state, action) => {
        const orgKey = toOrgKey(action.meta.arg.orgId);
        state.fetchingMembersByOrgId[orgKey] = true;
        state.fetchingMembersErrorByOrgId[orgKey] = null;
      })
      .addCase(fetchOrgMemberships.fulfilled, (state, action) => {
        const orgKey = toOrgKey(action.meta.arg.orgId);
        state.fetchingMembersByOrgId[orgKey] = false;
        state.fetchingMembersErrorByOrgId[orgKey] = null;
        state.hasFetchedMembersByOrgId[orgKey] = true;
        state.membersByOrgId[orgKey] = action.payload;
      })
      .addCase(fetchOrgMemberships.rejected, (state, action) => {
        const orgKey = toOrgKey(action.meta.arg.orgId);
        state.fetchingMembersByOrgId[orgKey] = false;
        state.hasFetchedMembersByOrgId[orgKey] = true;
        state.fetchingMembersErrorByOrgId[orgKey] =
          action.payload?.message || action.error.message || 'Failed to fetch organization members';
      })

      // =========================
      // MEMBERSHIPS: USER -> ORGS
      // =========================
      .addCase(fetchUserMemberships.pending, (state, action) => {
        const memberKey = toMemberKey(action.meta.arg.memberID);
        state.fetchingMembershipsByMemberId[memberKey] = true;
        state.fetchingMembershipsErrorByMemberId[memberKey] = null;
      })
      .addCase(fetchUserMemberships.fulfilled, (state, action) => {
        const memberKey = toMemberKey(action.meta.arg.memberID);
        state.fetchingMembershipsByMemberId[memberKey] = false;
        state.fetchingMembershipsErrorByMemberId[memberKey] = null;
        state.hasFetchedMembershipsByMemberId[memberKey] = true;
        state.membershipsByMemberId[memberKey] = action.payload;
      })
      .addCase(fetchUserMemberships.rejected, (state, action) => {
        const memberKey = toMemberKey(action.meta.arg.memberID);
        state.fetchingMembershipsByMemberId[memberKey] = false;
        state.hasFetchedMembershipsByMemberId[memberKey] = true;
        state.fetchingMembershipsErrorByMemberId[memberKey] =
          action.payload?.message || action.error.message || 'Failed to fetch user memberships';
      })

      // =========================
      // MEMBERSHIPS: UPDATE MEMBER
      // =========================
      .addCase(updateOrganizationMember.fulfilled, (state, action) => {
        const member = action.payload.member;
        if (!member) return;

        const orgKey = toOrgKey(action.meta.arg.orgID);
        const memberKey = toMemberKey(action.meta.arg.memberID);

        const existingOrgMembers = state.membersByOrgId[orgKey] ?? [];
        upsertMembershipInList(existingOrgMembers, member);
        state.membersByOrgId[orgKey] = existingOrgMembers;

        const existingUserMemberships = state.membershipsByMemberId[memberKey] ?? [];
        upsertMembershipInList(existingUserMemberships, member);
        state.membershipsByMemberId[memberKey] = existingUserMemberships;
      })
      .addCase(updateOrganizationMember.rejected, (state, action) => {
        state.error = action.payload?.message || action.error.message || 'Failed to update organization member';
      })

      // =========================
      // ADMIN: List all Organizations
      // =========================
      .addCase(listOrganizations.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(listOrganizations.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.hasListedAllOrganizations = true;
        upsertMany(state.items, action.payload);
      })
      .addCase(listOrganizations.rejected, (state, action) => {
        state.isLoading = false;
        state.hasListedAllOrganizations = true;
        state.error = action.payload?.message || action.error.message || 'Failed to list organizations';
      });
  },
});

export const {
  resetOrganizations,
  setOrganizations,
  upsertOrganization,
  upsertOrganizations,
  removeOrganization,
  setOrganizationsLoading,
  setOrganizationsError,
  clearSlugChecks,
  clearOrgMembersCache,
  clearUserMembershipsCache,
  setActiveOrganizationId,
} = organizationsSlice.actions;

export default organizationsSlice.reducer;

// =============================
// Selectors
// =============================

const selectOrganizationsState = (state: RootState) => state.organizations;

export const selectOrganizations = createSelector(selectOrganizationsState, (s) => s.items);
export const selectOrganizationsLoading = createSelector(selectOrganizationsState, (s) => s.isLoading);
export const selectOrganizationsError = createSelector(selectOrganizationsState, (s) => s.error);
export const selectOrganizationsHasFetched = createSelector(selectOrganizationsState, (s) => s.hasFetched);

// slug checks
export const selectSlugChecks = createSelector(selectOrganizationsState, (s) => s.slugChecks);
export const selectSlugChecksLoading = createSelector(selectOrganizationsState, (s) => s.slugChecksLoading);
export const selectSlugChecksError = createSelector(selectOrganizationsState, (s) => s.slugChecksError);

export const selectSlugCheckBySlug = (slug?: string) =>
  createSelector(selectSlugChecks, (slugChecks) => {
    if (!slug) return null;
    return slugChecks[slug.trim().toLowerCase()] ?? null;
  });

export const selectSlugCheckLoadingBySlug = (slug?: string) =>
  createSelector(selectSlugChecksLoading, (loadingMap) => {
    if (!slug) return false;
    return !!loadingMap[slug.trim().toLowerCase()];
  });

export const selectSlugCheckErrorBySlug = (slug?: string) =>
  createSelector(selectSlugChecksError, (errorMap) => {
    if (!slug) return null;
    return errorMap[slug.trim().toLowerCase()] ?? null;
  });

// org lookup
export const selectOrganizationById = (orgId?: string) =>
  createSelector(selectOrganizations, (items) => {
    if (!orgId) return null;
    return items.find((o) => o.id === orgId) ?? null;
  });

// =========================
// memberships selectors
// =========================

// org -> members[]
export const selectOrgMembersByOrgId = (orgId?: string) =>
  createSelector(selectOrganizationsState, (s) => {
    if (!orgId) return [];
    return s.membersByOrgId[orgId.trim()] ?? [];
  });

export const selectOrgMembersLoadingByOrgId = (orgId?: string) =>
  createSelector(selectOrganizationsState, (s) => {
    if (!orgId) return false;
    return !!s.fetchingMembersByOrgId[orgId.trim()];
  });

export const selectOrgMembersErrorByOrgId = (orgId?: string) =>
  createSelector(selectOrganizationsState, (s) => {
    if (!orgId) return null;
    return s.fetchingMembersErrorByOrgId[orgId.trim()] ?? null;
  });

export const selectHasFetchedOrgMembersByOrgId = (orgId?: string) =>
  createSelector(selectOrganizationsState, (s) => {
    if (!orgId) return false;
    return !!s.hasFetchedMembersByOrgId[orgId.trim()];
  });

// user -> memberships[]
export const selectUserMembershipsByMemberId = (memberID?: string) =>
  createSelector(selectOrganizationsState, (s) => {
    if (!memberID) return [];
    return s.membershipsByMemberId[memberID.trim()] ?? [];
  });

export const selectUserMembershipsLoadingByMemberId = (memberID?: string) =>
  createSelector(selectOrganizationsState, (s) => {
    if (!memberID) return false;
    return !!s.fetchingMembershipsByMemberId[memberID.trim()];
  });

export const selectUserMembershipsErrorByMemberId = (memberID?: string) =>
  createSelector(selectOrganizationsState, (s) => {
    if (!memberID) return null;
    return s.fetchingMembershipsErrorByMemberId[memberID.trim()] ?? null;
  });

export const selectHasFetchedUserMembershipsByMemberId = (memberID?: string) =>
  createSelector(selectOrganizationsState, (s) => {
    if (!memberID) return false;
    return !!s.hasFetchedMembershipsByMemberId[memberID.trim()];
  });
